import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import * as agentApi from '@/lib/agents/api';
import type { Agent, AgentExecution, SessionListItem } from '@/lib/agents/types';
import { AGENT_TYPE_INFO, LLM_PROVIDER_INFO } from '@/lib/agents/types';
import {
  ArrowLeft,
  Play,
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Trash2,
  Workflow,
  Save,
} from 'lucide-react';
import { AgentSettings } from '@/components/agents/agent-settings';
import { useCanvasStore } from '@/lib/agents/canvas-store';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

// Lazy load CanvasBuilder - it's heavy and only needed on the canvas tab
const CanvasBuilder = lazy(() =>
  import('@/components/agents/canvas/canvas-builder').then((mod) => ({
    default: mod.CanvasBuilder,
  }))
);

export const Route = createFileRoute('/_dashboard/agents/$id/')({
  component: AgentDetailPage,
});

function AgentDetailPage() {
  const navigate = useNavigate();
  const { id: agentId } = Route.useParams();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [executions, setExecutions] = useState<AgentExecution[]>([]);
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [canvasChanged, setCanvasChanged] = useState(false);
  const [savingCanvas, setSavingCanvas] = useState(false);

  const { importCanvas, exportCanvas } = useCanvasStore();

  const fetchAgent = useCallback(async () => {
    try {
      const response = await agentApi.getAgent(agentId);
      setAgent(response.data);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch agent');
      navigate({ to: '/agents' });
    }
  }, [agentId, navigate]);

  const fetchExecutions = useCallback(async () => {
    try {
      const response = await agentApi.listExecutions(agentId, { limit: 10 });
      setExecutions(response.data);
    } catch (error) {
      console.error('Failed to fetch executions:', error);
    }
  }, [agentId]);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await agentApi.listSessions(agentId, { limit: 10 });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, [agentId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchAgent(), fetchExecutions(), fetchSessions()]).finally(() =>
      setLoading(false)
    );
  }, [fetchAgent, fetchExecutions, fetchSessions]);

  // Load canvas data when agent is loaded
  useEffect(() => {
    if (agent?.canvas_data) {
      importCanvas(agent.canvas_data);
      setCanvasChanged(false);
    }
  }, [agent?.id]);

  const handleClearSession = async (sessionId: string) => {
    if (!confirm('Clear this session memory?')) return;

    try {
      await agentApi.clearSession(agentId, sessionId);
      toast.success('Session cleared');
      fetchSessions();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to clear session');
    }
  };

  const handleSaveCanvas = async () => {
    if (!agent) return;

    setSavingCanvas(true);
    try {
      const canvasData = exportCanvas();
      const response = await agentApi.updateAgent(agentId, {
        canvasData,
      });
      setAgent(response.data);
      setCanvasChanged(false);
      toast.success('Canvas saved successfully');
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save canvas');
    } finally {
      setSavingCanvas(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48 mt-2" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!agent) return null;

  const typeInfo = AGENT_TYPE_INFO[agent.agent_type];
  const providerInfo = LLM_PROVIDER_INFO[agent.config.llmProvider];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/agents">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{agent.name}</h1>
              <Badge variant="outline">{typeInfo.label}</Badge>
              {!agent.is_active && <Badge variant="destructive">Inactive</Badge>}
            </div>
            <p className="text-muted-foreground">
              {agent.description || typeInfo.description}
            </p>
          </div>
        </div>
        <Button asChild>
          <Link to={`/agents/${agentId}/chat` as any}>
            <Play className="mr-2 h-4 w-4" />
            Start Chat
          </Link>
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {agent.canvas_data && (
            <TabsTrigger value="canvas" className="flex items-center gap-1.5">
              <Workflow className="h-3.5 w-3.5" />
              Canvas
              {canvasChanged && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
            </TabsTrigger>
          )}
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Canvas Tab */}
        {agent.canvas_data && (
          <TabsContent value="canvas" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Workflow Canvas</h2>
                <p className="text-sm text-muted-foreground">
                  Edit the visual workflow for this agent
                </p>
              </div>
              <Button
                onClick={handleSaveCanvas}
                disabled={!canvasChanged || savingCanvas}
              >
                {savingCanvas ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Canvas
              </Button>
            </div>
            <div className="border rounded-lg overflow-hidden h-[calc(100vh-16rem)] min-h-[500px]">
              <Suspense fallback={
                <div className="h-full w-full flex items-center justify-center bg-muted/30">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              }>
                <CanvasBuilder onChange={() => setCanvasChanged(true)} />
              </Suspense>
            </div>
          </TabsContent>
        )}

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Provider</span>
                  <span className="font-medium">{providerInfo.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Model</span>
                  <span className="font-medium">{agent.config.model || 'Default'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Temperature</span>
                  <span className="font-medium">{agent.config.temperature ?? 0.7}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Max Iterations</span>
                  <span className="font-medium">{agent.config.maxIterations || 10}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tools</span>
                  <span className="font-medium">{agent.config.tools?.length || 0} enabled</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Executions</span>
                  <span className="font-medium">{executions.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Active Sessions</span>
                  <span className="font-medium">{sessions.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span className="font-medium">
                    {formatDistanceToNow(new Date(agent.created_at), { addSuffix: true })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Updated</span>
                  <span className="font-medium">
                    {formatDistanceToNow(new Date(agent.updated_at), { addSuffix: true })}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {agent.config.systemPrompt && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">System Prompt</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-sm bg-muted p-4 rounded-lg whitespace-pre-wrap">
                  {agent.config.systemPrompt}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Executions</CardTitle>
              <CardDescription>Last 10 agent executions</CardDescription>
            </CardHeader>
            <CardContent>
              {executions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No executions yet</p>
              ) : (
                <div className="space-y-2">
                  {executions.map((exec) => (
                    <div
                      key={exec.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {getStatusIcon(exec.status)}
                        <div>
                          <p className="font-medium text-sm line-clamp-1">
                            {exec.input.message.slice(0, 50)}...
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(exec.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        {exec.duration_ms && (
                          <p className="text-muted-foreground">{exec.duration_ms}ms</p>
                        )}
                        {exec.usage && (
                          <p className="text-xs text-muted-foreground">
                            {exec.usage.totalTokens} tokens
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Active Sessions</CardTitle>
              <CardDescription>Conversation sessions with memory</CardDescription>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No sessions yet</p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm font-mono">{session.session_id}</p>
                          <p className="text-xs text-muted-foreground">
                            {session.message_count} messages &bull;{' '}
                            {formatDistanceToNow(new Date(session.updated_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleClearSession(session.session_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <AgentSettings
            agent={agent}
            onUpdate={(updatedAgent) => setAgent(updatedAgent)}
            onDelete={() => navigate({ to: '/agents' })}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
